/**
 * BerichtComposer — bericht aan kantoor sturen (uitvoering → kantoor).
 *
 * Vervangt SendMessageModal + NieuweVraagSheet. Mobiel-first bottom-sheet.
 * - Met een werkOrder.id: toont een Wasserij_item-kiezer binnen die werkbon.
 * - Zonder werkOrder: algemeen bericht (optioneel WO-nummer als vrije tekst).
 */

import { useEffect, useMemo, useState } from 'react'
import { BerichtError, createBericht, uploadBerichtFoto, type Prioriteit } from '../../lib/berichten'
import { salesforceClient } from '../../lib/salesforce/client'
import type { WasserijItem } from '../../lib/salesforce/types'

interface WerkOrderInfo {
  id?: string | null
  name?: string | null
  customerName?: string | null
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSent: () => void
  /** Pre-fill vanuit route-stop / WO-pagina. */
  werkOrder?: WerkOrderInfo
  /** Pre-selected item vanuit voorinspectie/reinigen. */
  wasserijItem?: { id: string | null; name: string | null }
}

const NO_ITEM = '__none__'

function pickItemLabel(item: WasserijItem): string {
  const naam = item.Product__r?.Name ?? item.Name ?? 'Onbekend kleed'
  const dim =
    item.Lengte__c && item.Breedte__c
      ? ` — ${item.Lengte__c}×${item.Breedte__c} cm`
      : item.Diameter__c
        ? ` — ⌀${item.Diameter__c} cm`
        : ''
  return `${naam}${dim}`
}

export function BerichtComposer({ isOpen, onClose, onSent, werkOrder, wasserijItem }: Props) {
  const [prioriteit, setPrioriteit] = useState<Prioriteit>('normaal')
  const [message, setMessage] = useState('')
  const [woName, setWoName] = useState(werkOrder?.name || '')
  const [customerName, setCustomerName] = useState(werkOrder?.customerName || '')
  const [fotoPaths, setFotoPaths] = useState<string[]>([])
  const [fotoPreviews, setFotoPreviews] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const werkOrderId = werkOrder?.id || null
  const hasWerkOrderId = !!werkOrderId

  const [selectedItemId, setSelectedItemId] = useState<string>(wasserijItem?.id ?? NO_ITEM)
  const [items, setItems] = useState<WasserijItem[]>([])
  const [itemsLoading, setItemsLoading] = useState(hasWerkOrderId)

  useEffect(() => {
    if (!isOpen || !werkOrderId) return
    let cancelled = false
    setItemsLoading(true)
    salesforceClient
      .getWasserijItems(werkOrderId)
      .then((res) => {
        if (cancelled) return
        if (res.success && res.data) {
          const primary = res.data.filter((i) => !i.Gerelateerd_Wasserij_Item__c)
          setItems(primary)
          if (wasserijItem?.id && !primary.some((i) => i.Id === wasserijItem.id)) {
            setSelectedItemId(NO_ITEM)
          }
        }
      })
      .catch(() => {
        /* picker niet beschikbaar → bericht op WO-niveau */
      })
      .finally(() => {
        if (!cancelled) setItemsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, werkOrderId, wasserijItem?.id])

  const selectedItem = useMemo(
    () => (selectedItemId !== NO_ITEM ? items.find((i) => i.Id === selectedItemId) ?? null : null),
    [items, selectedItemId]
  )

  if (!isOpen) return null

  const effectiveItemId = selectedItemId !== NO_ITEM ? selectedItemId : null
  const effectiveItemName = selectedItem
    ? selectedItem.Product__r?.Name ?? selectedItem.Name ?? null
    : selectedItemId === wasserijItem?.id
      ? wasserijItem?.name ?? null
      : null

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setUploading(true)
    setError(null)
    try {
      for (const file of Array.from(files)) {
        const result = await uploadBerichtFoto(file)
        setFotoPaths((p) => [...p, result.path])
        setFotoPreviews((p) => [...p, result.url])
      }
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Foto-upload mislukt')
    } finally {
      setUploading(false)
    }
  }

  function reset() {
    setMessage('')
    setPrioriteit('normaal')
    setWoName('')
    setCustomerName('')
    setFotoPaths([])
    setFotoPreviews([])
    setError(null)
    setSuccess(false)
  }

  function handleClose() {
    if (saving) return
    reset()
    onClose()
  }

  async function handleSend() {
    if (!message.trim()) return
    setSaving(true)
    setError(null)
    try {
      await createBericht({
        werkOrderId,
        werkOrderName: hasWerkOrderId ? werkOrder?.name ?? null : woName.trim() || null,
        customerName: hasWerkOrderId ? werkOrder?.customerName ?? null : customerName.trim() || null,
        wasserijItemId: effectiveItemId,
        wasserijItemName: effectiveItemName,
        prioriteit,
        body: message.trim(),
        fotoUrls: fotoPaths,
      })
      setSuccess(true)
      setTimeout(() => {
        reset()
        onSent()
        onClose()
      }, 800)
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Verzenden mislukt')
    } finally {
      setSaving(false)
    }
  }

  const scopeLabel = effectiveItemName
    ? `${effectiveItemName} (${werkOrder?.name ?? werkOrderId})`
    : hasWerkOrderId
      ? `Hele werkbon (${werkOrder?.name ?? werkOrderId})`
      : 'Algemeen bericht'

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40" onClick={handleClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-2xl shadow-xl max-h-[90vh] flex flex-col"
      >
        <header className="px-md py-md border-b border-kleda-border flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-kleda-text-main text-base">Bericht aan kantoor</h2>
            <p className="text-xs text-kleda-text-muted mt-0.5 truncate">{scopeLabel}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-kleda-text-muted hover:text-kleda-text-main text-2xl leading-none -mt-1 px-1"
            aria-label="Sluiten"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-md py-md space-y-md">
          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700">
              <span className="text-sm font-medium">Bericht verstuurd!</span>
            </div>
          )}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
          )}

          {hasWerkOrderId ? (
            <label className="block">
              <span className="text-xs font-medium text-kleda-text-main">Voor welk item?</span>
              <select
                value={selectedItemId}
                onChange={(e) => setSelectedItemId(e.target.value)}
                disabled={itemsLoading}
                className="mt-1 w-full border border-kleda-border rounded px-2 py-2 text-sm bg-white"
              >
                <option value={NO_ITEM}>📋 Hele werkbon (geen specifiek item)</option>
                {items.map((item) => (
                  <option key={item.Id} value={item.Id}>
                    {pickItemLabel(item)}
                  </option>
                ))}
              </select>
              {itemsLoading && <p className="mt-1 text-[11px] text-kleda-text-muted">Items laden…</p>}
            </label>
          ) : (
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-kleda-text-main">
                  WO-nummer <span className="text-kleda-text-muted font-normal">(optioneel)</span>
                </span>
                <input
                  type="text"
                  value={woName}
                  onChange={(e) => setWoName(e.target.value)}
                  placeholder="WO-12345"
                  className="mt-1 w-full px-3 py-2.5 border border-kleda-border rounded-xl text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-kleda-text-main">
                  Klantnaam <span className="text-kleda-text-muted font-normal">(optioneel)</span>
                </span>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Naam klant"
                  className="mt-1 w-full px-3 py-2.5 border border-kleda-border rounded-xl text-sm"
                />
              </label>
            </div>
          )}

          <label className="block">
            <span className="text-xs font-medium text-kleda-text-main">Prioriteit</span>
            <div className="mt-1 flex gap-2">
              {(['normaal', 'urgent'] as Prioriteit[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPrioriteit(p)}
                  className={`flex-1 text-sm px-3 py-2 rounded border ${
                    prioriteit === p
                      ? 'border-kleda-primary bg-kleda-primary text-white'
                      : 'border-kleda-border bg-white text-kleda-text-main'
                  }`}
                >
                  {p === 'urgent' ? 'Urgent' : 'Normaal'}
                </button>
              ))}
            </div>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-kleda-text-main">Bericht</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              maxLength={5000}
              placeholder="Wat moet kantoor weten?"
              className="mt-1 w-full border border-kleda-border rounded px-2 py-2 text-sm"
              autoFocus
            />
          </label>

          <div>
            <span className="text-xs font-medium text-kleda-text-main">Foto's (optioneel)</span>
            <div className="mt-1 flex flex-wrap gap-2">
              {fotoPreviews.map((url, idx) => (
                <div key={fotoPaths[idx]} className="relative">
                  <img src={url} alt="" className="w-16 h-16 object-cover rounded border border-kleda-border" />
                  <button
                    type="button"
                    onClick={() => {
                      setFotoPaths((p) => p.filter((_, i) => i !== idx))
                      setFotoPreviews((p) => p.filter((_, i) => i !== idx))
                    }}
                    className="absolute -top-1 -right-1 bg-white border border-kleda-border rounded-full w-5 h-5 text-xs leading-none"
                  >
                    ×
                  </button>
                </div>
              ))}
              <label
                className={`w-16 h-16 border-2 border-dashed border-kleda-border rounded flex items-center justify-center text-2xl text-kleda-text-muted ${
                  uploading ? 'opacity-50' : ''
                }`}
              >
                +
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={(e) => handleFiles(e.target.files)}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
            {uploading && <p className="mt-1 text-xs text-kleda-text-muted">Foto uploaden…</p>}
          </div>
        </div>

        <footer className="px-md py-md border-t border-kleda-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="text-sm text-kleda-text-muted hover:text-kleda-text-main px-3 py-2"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={saving || uploading || success || !message.trim()}
            className="bg-kleda-primary text-white text-sm font-medium px-4 py-2 rounded disabled:bg-gray-300"
          >
            {saving ? 'Versturen…' : 'Verstuur'}
          </button>
        </footer>
      </div>
    </div>
  )
}
